<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold">Управление пользователями</h1>

    <!-- Статистика -->
    <div class="grid grid-cols-3 gap-6">
      <div class="bg-white p-6 rounded-lg shadow">
        <p class="text-gray-600 text-sm">Всего пользователей</p>
        <p class="text-4xl font-bold">{{ users.length }}</p>
      </div>
      <div class="bg-white p-6 rounded-lg shadow">
        <p class="text-gray-600 text-sm">Активных</p>
        <p class="text-4xl font-bold">{{ activeUsers }}</p>
      </div>
      <div class="bg-white p-6 rounded-lg shadow">
        <p class="text-gray-600 text-sm">Неактивных</p>
        <p class="text-4xl font-bold">{{ inactiveUsers }}</p>
      </div>
    </div>

    <!-- Таблица пользователей -->
    <div class="bg-white rounded-lg shadow">
      <div class="p-6 border-b">
        <h2 class="text-xl font-bold">Пользователи</h2>
      </div>

      <div v-if="loading" class="p-6 text-center text-gray-600">
        Загрузка пользователей...
      </div>

      <div v-else-if="users.length === 0" class="p-6 text-center text-gray-600">
        Пользователей не найдено
      </div>

      <table v-else class="w-full">
        <thead class="bg-gray-50 border-b">
          <tr>
            <th class="px-6 py-3 text-left text-sm font-bold">Имя</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Email</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Телефон</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Город</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Роль</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Статус</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Дата регистрации</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Действия</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr v-for="user in users" :key="user._id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-bold">{{ user.name }}</td>
            <td class="px-6 py-4 text-sm">{{ user.email }}</td>
            <td class="px-6 py-4 text-sm">{{ user.phone || '-' }}</td>
            <td class="px-6 py-4 text-sm">{{ user.city || '-' }}</td>
            <td class="px-6 py-4">
              <span :class="getRoleClass(user.role)" class="px-3 py-1 rounded-full text-xs font-bold">
                {{ getRoleLabel(user.role) }}
              </span>
            </td>
            <td class="px-6 py-4">
              <span
                :class="user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                class="px-3 py-1 rounded-full text-xs font-bold"
              >
                {{ user.isActive ? 'Активен' : 'Неактивен' }}
              </span>
            </td>
            <td class="px-6 py-4 text-sm text-gray-600">
              {{ formatDate(user.createdAt) }}
            </td>
            <td class="px-6 py-4">
              <div class="flex gap-2">
                <button
                  @click="toggleUserStatus(user._id, user.isActive)"
                  :class="user.isActive ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'"
                  class="px-3 py-1 rounded text-xs hover:opacity-80"
                >
                  {{ user.isActive ? 'Отключить' : 'Включить' }}
                </button>
                <button
                  @click="viewUserOrders(user._id)"
                  class="bg-blue-100 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-200"
                >
                  Заказы
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../../stores/auth';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { uk } from 'date-fns/locale';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default {
  name: 'AdminUsers',
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();
    const users = ref([]);
    const loading = ref(false);

    const activeUsers = computed(() => users.value.filter(u => u.isActive).length);
    const inactiveUsers = computed(() => users.value.filter(u => !u.isActive).length);

    const roleLabels = {
      user: 'Пользователь',
      admin: 'Администратор',
      technician: 'Техник'
    };

    const loadUsers = async () => {
      loading.value = true;
      try {
        // TODO: Создать API маршрут для получения всех пользователей (админу)
        // Пока используем заглушку с тестовыми данными
        const testUsers = [
          {
            _id: '1',
            name: 'Иван Петренко',
            email: 'ivan@example.com',
            phone: '+38 050 123-45-67',
            city: 'Киев',
            role: 'user',
            isActive: true,
            createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          },
          {
            _id: '2',
            name: 'Мария Сидорова',
            email: 'maria@example.com',
            phone: '+38 067 234-56-78',
            city: 'Харьков',
            role: 'user',
            isActive: true,
            createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000)
          },
          {
            _id: '3',
            name: 'Олег Ковалюк',
            email: 'oleg@example.com',
            phone: '+38 093 345-67-89',
            city: 'Львов',
            role: 'technician',
            isActive: true,
            createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
          }
        ];
        users.value = testUsers;
      } catch (error) {
        console.error('Ошибка при загрузке пользователей:', error);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (date) => {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: uk });
    };

    const getRoleLabel = (role) => roleLabels[role] || role;
    const getRoleClass = (role) => {
      const classes = {
        user: 'bg-blue-100 text-blue-700',
        admin: 'bg-red-100 text-red-700',
        technician: 'bg-purple-100 text-purple-700'
      };
      return classes[role] || 'bg-gray-100 text-gray-700';
    };

    const toggleUserStatus = async (userId, currentStatus) => {
      // TODO: Реализовать API для изменения статуса пользователя
      alert('Функция будет реализована в API');
    };

    const viewUserOrders = (userId) => {
      // TODO: Реализовать просмотр заказов пользователя
      alert('Функция будет реализована');
    };

    onMounted(() => {
      loadUsers();
    });

    return {
      users,
      loading,
      activeUsers,
      inactiveUsers,
      formatDate,
      getRoleLabel,
      getRoleClass,
      toggleUserStatus,
      viewUserOrders
    };
  }
};
</script>
